import { useStyletron } from 'baseui';
import { Avatar } from 'baseui/avatar';
import { Block } from 'baseui/block';
import { Button, SHAPE } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { Card } from 'baseui/card';
import { Delete } from 'baseui/icon';
import { DURATION, useSnackbar } from 'baseui/snackbar';
import { HeadingLarge, LabelSmall, ParagraphMedium } from 'baseui/typography';
import { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { loadSolutionById, loadUserSolutions } from '../application/actions/solutions';
import { cacheView, setView } from '../application/actions/ui';
import { getUserSolutions } from '../application/selectors/solutions';
import { getCurrentView } from '../application/selectors/ui';
import { getUserProfile } from '../application/selectors/users';
import useLocale from '../hooks/useLocale';


const ReDoProfile = ({ profile, onClose }) => {
  const dispatch = useDispatch();
  const [, theme] = useStyletron();
  const { t } = useLocale();
  const { enqueue } = useSnackbar();
  const [selectedTab, setSelectedTab] = useState('info');
  const [loadingId, setLoadingId] = useState(null);
  const ownProfile = useSelector(getUserProfile);
  const currentView = useSelector(getCurrentView);

  const userProfile = profile || ownProfile;
  const userSolutions = useSelector(getUserSolutions);

  const onInfoClicked = () => {
    setSelectedTab('info');
  }

  const onSolutionsClicked = () => {
    dispatch(loadUserSolutions(userProfile.userId));
    setSelectedTab('solutions');
  }

  const onSolutionClick = (solution) => {
    if (loadingId) return;
    setLoadingId(solution.id);
    dispatch(loadSolutionById(solution.id))
      .then(full => {
        const navEntry = { view: 'solution', payload: { solution: full } };
        dispatch(cacheView(currentView));
        try { window.history.pushState(navEntry, null); } catch (e) {}
        dispatch(setView(navEntry));
        onClose?.();
      })
      .catch(() => {
        enqueue({ message: 'Could not load solution', dismissAfter: DURATION.short, startEnhancer: () => <Delete size={theme.sizing.scale800} /> });
      })
      .finally(() => setLoadingId(null));
  }

  return (
    <Block display='flex' flexDirection='column'>
      <Block alignSelf='center'>
        <Avatar
          name={userProfile.fullname}
          size="96px"
          src={userProfile.photo}
        />
      </Block>
      <Block alignSelf='center' display='flex' flexDirection='column' alignItems='center'>
        <HeadingLarge marginBottom='0' overrides={{ Block: { style: { fontWeight: 'bold' } } }}>{userProfile.fullname}</HeadingLarge>
        <ParagraphMedium>{userProfile.city}, {userProfile.country}</ParagraphMedium>
      </Block>
      <Block alignSelf='center' marginTop='16px'>
        <ButtonGroup overrides={{ Root: { style: { width: 'fit-content', height: '42px', justifyContent: 'center', margin: '0', gap: '1ch' } } }} shape={SHAPE.pill}>
          <Button onClick={onInfoClicked} overrides={{ BaseButton: { style: { fontWeight: selectedTab === 'info' ? 'bold' : '500', boxShadow: 'none', width: '105px', backgroundColor: selectedTab === 'info' ? 'black' : 'transparent', border: selectedTab !== 'info' ? '1px solid black' : 'none' } } }} isSelected={selectedTab === 'info'}>{t('profile.tab_info')}</Button>
          <Button onClick={onSolutionsClicked} overrides={{ BaseButton: { style: { fontWeight: selectedTab === 'solutions' ? 'bold' : '500', boxShadow: 'none', width: '105px', backgroundColor: selectedTab === 'solutions' ? 'black' : 'transparent', border: selectedTab !== 'solutions' ? '1px solid black' : 'none' } } }} isSelected={selectedTab === 'solutions'}>{t('profile.tab_solutions')}</Button>
        </ButtonGroup>
      </Block>
      {selectedTab === 'info' &&
        <>
          <Block marginTop='48px'>
            <LabelSmall color={theme.colors.primary200}>{t('profile.label_about_me')}</LabelSmall>
            <ParagraphMedium marginBottom='0' marginTop='8px'>{userProfile.about}</ParagraphMedium>
          </Block>
          <Block marginTop='48px'>
            <LabelSmall color={theme.colors.primary200}>{t('profile.label_skills')}</LabelSmall>
            <ParagraphMedium marginBottom='0' marginTop='8px'>{userProfile.skills?.join(', ')}</ParagraphMedium>
          </Block>
        </>
      }
      {selectedTab === 'solutions' &&
        <>
          <Block display='grid' gridTemplateColumns='1fr 1fr' flexWrap='wrap' marginTop='48px' gridGap='1ch' padding='8px'>
            {userSolutions.map((solution) => (
              <div key={solution.id} onClick={() => onSolutionClick(solution)} style={{ opacity: loadingId === solution.id ? 0.5 : 1, pointerEvents: loadingId ? 'none' : 'auto' }}>
                <Card
                  overrides={{
                    Root: { style: { cursor: loadingId ? 'default' : 'pointer', border: '0', height: '150px', display: 'flex', flexDirection: 'column', flex: '1', boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)` } },
                    Title: { style: { fontSize: '16px', marginTop: '0', marginBottom: '0', lineHeight: '1', padding: '8px !important' } },
                    Contents: { style: { marginTop: '0', marginBottom: '0', marginLeft: '0', marginRight: '0' } },
                    HeaderImage: { style: { height: '75%', objectFit: 'cover' } },
                    Body: { style: { display: 'none' } }
                  }}
                  headerImage={solution.photos?.[0]?.photo}
                  title={solution.title}
                />
              </div>))
            }
          </Block>

        </>
      }
    </Block>
  )
}

export default ReDoProfile;
